import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Modal, Button} from 'react-bootstrap';
import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import DatePicker from "react-date-picker";
import {useUsers} from "../context/UsersContext";
import {useUserCommentHistory} from "../hooks/api/userCommentHistory";
import ListBottomPager from "../components/ListBottomPager";

const UserHistory = () => {
    const navigate = useNavigate();
    const {t} = useTranslation();
    const {id} = useParams();
    const {users} = useUsers();

    const [date, setDate] = useState(null);
    const [page, setPage] = useState(1);

    const [{result, loading}, {fetchHistory}] = useUserCommentHistory();

    const user = useMemo(
        () => (users ? users.find(u => u.id === parseInt(id, 10)) : null),
        [users, id]
    );

    useEffect(() => {
        fetchHistory(id, {date, page});
    }, [fetchHistory, id, date, page]);

    const handleClose = useCallback(() => {
        navigate('/user');
    }, [navigate]);

    const handleDateChange = (value) => {
        setDate(value);
        setPage(1);
    };

    const formatDayTitle = (dateString) => {
        // dateString format: "Y-m-d H:i:s"
        const [datePart] = dateString.split(' ');
        const [y, m, d] = datePart.split('-');
        return `${d}/${m}/${y}`;
    };

    const formatTime = (dateString) => {
        const [, timePart] = dateString.split(' ');
        return timePart.substring(0, 5);
    };

    const groupedRows = useMemo(() => {
        if (!result || !result.comments) {
            return [];
        }
        const rows = [];
        let currentDay = null;
        result.comments.forEach((comment) => {
            const day = formatDayTitle(comment.createdAt);
            if (day !== currentDay) {
                rows.push({type: 'header', key: `h-${day}`, day});
                currentDay = day;
            }
            rows.push({type: 'comment', key: `c-${comment.id}`, comment});
        });
        return rows;
    }, [result]);

    return (
        <Modal show={true} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    {user
                        ? t('users.history.title', {firstName: user.firstName, lastName: user.lastName})
                        : t('users.history.head_title')}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <label className="form-label">{t('users.history.filter.date.label')}</label>
                    <DatePicker
                        onChange={handleDateChange}
                        value={date}
                        className="p-0 form-control form-control-lg"
                        format="dd-MM-y"
                    />
                </div>

                {!loading && groupedRows.length === 0 && (
                    <p className="text-muted">{t('users.history.empty')}</p>
                )}

                <ul className="timeline mt-2 mb-0">
                    {groupedRows.map(row => {
                        if (row.type === 'header') {
                            return (
                                <li key={row.key} className="timeline-day-title mt-3">
                                    <h6 className="mb-1">{row.day}</h6>
                                </li>
                            );
                        }
                        const {comment} = row;
                        return (
                            <li className="timeline-item" key={row.key}>
                                <strong>
                                    {user ? `${user.firstName} ${user.lastName}` : ''}
                                </strong>
                                {comment.isAutomatic && (
                                    <small> ({t('users.history.auto_badge')})</small>
                                )}
                                <span className="float-end text-muted text-sm">
                                    {formatTime(comment.createdAt)}
                                </span>
                                <div className="text-muted small">
                                    {comment.tiersPayant?.patientName}
                                    {comment.tiersPayant?.invoiceNumber && (
                                        <> — {t('users.history.invoice_label', {number: comment.tiersPayant.invoiceNumber})}</>
                                    )}
                                </div>
                                <p className={comment.isAutomatic ? 'fst-italic mb-0' : 'mb-0'}>
                                    {comment.comment}
                                </p>
                            </li>
                        );
                    })}
                </ul>

                {result?.metaData && result.metaData.nbTotal > 0 && (
                    <ListBottomPager metaData={result.metaData} handlePage={setPage} />
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    {t('users.history.close')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default UserHistory;
